/**
 * AdmSettingsSurveyWidget
 * Customize survey options
 * Can be save for user options as for account options for the display settings
 */

import { connect } from "react-redux"
import { store } from "@/index"
import { withTranslation, WithTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import Space from "@/components/space"
import { Session, SESSION_COLORS } from "@/redux/_session.types"
import { sessionEdit, sessionEditAccountOptions } from "@/redux/_session.actions"
import ColorPickerWidget from "@/widgets/color-picker.widget"
import MultiRangeSlider from "multi-range-slider-react"
import Card from "@/components/card"
import ListItem from "@/components/list-item"
import Checkbox from "@/components/checkbox"
import NeutralGaugePreview from "@/components/neutral-gauge-preview"
import Chip from "@/components/chip"
import { useEffect, useState } from "react"
import Dropdown from "@/components/dropdown"
import getUserFilterDates, { UserFilterDates } from "@/utils/get-user-filter-dates.utils"
import TextInput from "@/components/text-input"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faInfoCircle, faTimes } from "@fortawesome/free-solid-svg-icons"
import { toast } from "react-toastify"
import SaveToast from "@/components/SaveToast"
import { ACCOUNT_RESULTS_TYPES, AccountOptions, AccountState } from "@/redux/account.types"
import { UserOptions } from "@/redux/user.types"
import Tabs from "@/components/tabs"
import { STATUS_SAVED, STATUS_SAVING } from "@/redux/_status.types"
import { StatusType } from "@/core/factory/types"
import { accountStatus, accountUpdateOptions } from "@/redux/account.actions"
import { userUpdateOptions } from "@/redux/user.actions"
import Range from "@/components/range"
import { isRepartitionCustom } from "@/utils/get-repartition-ceils.utils"
import AdmSettingsRepartitionScaleWidget from "./adm-settings-repartition-scale.widget"
import AdmFiltersOrderWidget from "./adm-filters-order.widget"
import AdmFiltersDefaultWidget from "./adm-filters-default.widget"
import { GollacFiltersWidget } from "./gollac-filters.widget"
import ObserverFeaturesWidget from "./observer-features.widget"
import { filterFetch, filterGet } from "@/redux/filter.actions"
import { FilterState } from "@/redux/filter.types"

interface StateProps extends WithTranslation {
  account: AccountState
  filter: FilterState
  _session: Session
}

interface OwnProps {
  isSmall?: boolean
}

type Props = StateProps & OwnProps

const SCORE_DISPLAY_MODE: any = [
  { id: "note", name: "score_display_note" },
  { id: "satisfaction", name: "score_display_satisfaction" }
]

const MIN_CEILS: number = 1
const MAX_CEILS: number = 10
const CEIL_BIRTHDATE_MIN: number = 18
const CEIL_BIRTHDATE_MAX: number = 80
const CEIL_COMPANYWELCOMEDATE_MIN: number = 2
const CEIL_COMPANYWELCOMEDATE_MAX: number = 300
const TAB_DISPLAY_ACCOUNT = "TAB_DISPLAY_ACCOUNT"
const TAB_DISPLAY_USER = "TAB_DISPLAY_USER"

function AdmSettingsSurveyWidget(props: Props) {
  const { t } = props
  const navigate = useNavigate()

  //Current tab for the display card
  //By default it is user options that can be displayed
  const [currentDisplayTab, setCurrentDisplayTab] = useState(
    props._session.userRole === "ADMIN" ? TAB_DISPLAY_ACCOUNT : TAB_DISPLAY_USER
  )

  //Show save buttons
  const [isEdited, setIsEdited] = useState(false)

  //Account options that will be edited
  const [accountOptions, setAccountOptions] = useState<AccountOptions>(props._session.accountOptions)

  //Idem for user options
  const [userOptions, setUserOptions] = useState(props._session.userOptions)

  //Local benchmark visibility (localStorage, no API)
  const [benchmarkLocalVisible, setBenchmarkLocalVisible] = useState(
    () => localStorage.getItem("benchmark_visible") !== "false"
  )

  //Load filters when page is loaded
  useEffect(() => {
    load()
    async function load() {
      const filters: any = await store.dispatch(filterFetch(true, false))
      store.dispatch(filterGet(filters.error ? [] : filters))
    }
  }, [])

  //Change global options for the account
  async function updateOptions(key, value) {
    const response: any = store.dispatch(
      accountUpdateOptions(
        new AccountOptions({
          ...props._session.accountOptions,
          [key]: value
        })
      )
    )

    if (!response.error) {
      toast(t("utils_saved"))
      store.dispatch(sessionEditAccountOptions(key, value))
    }
  }

  //On click cancel
  //Reset all parameters from the store
  function cancel() {
    setIsEdited(false)
    setUserOptions(props._session.userOptions)
    setAccountOptions(props._session.accountOptions)
  }

  //Edit settings for the account
  //Specific case for ceilsRepartition
  function edit(key: string, value: any) {
    const options = new AccountOptions(accountOptions)
    options[key] = value
    setIsEdited(true)
    setAccountOptions(options)
  }

  //Edit settings for the display
  //According the tab, even the userOptions or
  function editDisplay(key: string, value: any) {
    if (currentDisplayTab === TAB_DISPLAY_USER) {
      const options = new UserOptions(userOptions)
      setUserOptions(getDisplayOptions(key, value, options))
    } else {
      const options = new AccountOptions(accountOptions)
      setAccountOptions(getDisplayOptions(key, value, options))
    }

    setIsEdited(true)

    function getDisplayOptions(key: string, value: any, options: any) {
      if (key === "ceilsRepartitionScale") {
        options.ceilsRepartition = value.ceils
        options.ceilsRepartitionLabels = value.labels
      } else if (key === "ceilsRepartitionGollacScale") {
        options.ceilsRepartitionGollac = value.ceils
        options.ceilsRepartitionGollacLabels = value.labels
      } else if (key === "dashboardNightmareMode") {
        options[key + "Min"] = value.minValue
        options[key + "Max"] = value.maxValue
      } else {
        options[key] = value
      }
      return options
    }
  }

  function changeBirthDateIntervals(values: number[]) {
    edit("ceilsBirthDate", values)
  }

  function changeCompanyWelcomeDateIntervals(values: number[]) {
    edit("ceilsCompanyWelcomeDate", values)
  }

  function addBirthDateCeil() {
    const array: number[] = accountOptions.ceilsBirthDate

    let random: number = -1
    do {
      random = Math.floor(Math.random() * (CEIL_BIRTHDATE_MAX - CEIL_BIRTHDATE_MIN + 1)) + CEIL_BIRTHDATE_MIN
    } while (array.includes(random)) // will return false if random isn't asigned

    array.push(random)

    changeBirthDateIntervals(array.sort((a: number, b: number) => a - b))
  }

  function addCompanyWelcomeDateCeil() {
    const array: number[] = accountOptions.ceilsCompanyWelcomeDate

    let random: number = -1
    do {
      random =
        Math.floor(Math.random() * (CEIL_COMPANYWELCOMEDATE_MAX - CEIL_COMPANYWELCOMEDATE_MIN + 1)) +
        CEIL_COMPANYWELCOMEDATE_MIN
    } while (array.includes(random)) // will return false if random isn't asigned

    array.push(random)

    changeCompanyWelcomeDateIntervals(array.sort((a: number, b: number) => a - b))
  }

  function getBirthDateMinAllowed(index: number): number {
    return index === 0 ? CEIL_BIRTHDATE_MIN : accountOptions.ceilsBirthDate[index - 1] + 1
  }

  function getBirthDateMaxAllowed(index: number): number {
    return index === accountOptions.ceilsBirthDate.length - 1
      ? CEIL_BIRTHDATE_MAX
      : accountOptions.ceilsBirthDate[index + 1] - 1
  }

  function getCompanyWelcomeDateMinAllowed(index: number): number {
    return index === 0 ? CEIL_COMPANYWELCOMEDATE_MIN : accountOptions.ceilsCompanyWelcomeDate[index - 1] + 1
  }

  function getCompanyWelcomeDateMaxAllowed(index: number): number {
    return index === accountOptions.ceilsCompanyWelcomeDate.length - 1
      ? CEIL_COMPANYWELCOMEDATE_MAX
      : accountOptions.ceilsCompanyWelcomeDate[index + 1] - 1
  }

  function getPreferencesDisplay() {
    const options = currentDisplayTab === TAB_DISPLAY_USER ? userOptions : accountOptions

    return (
      <div>
        {currentDisplayTab === TAB_DISPLAY_USER && (
          <ListItem>
            <Checkbox
              active={!userOptions.dashboardApplyUserSettings}
              text={t("account_settings_use_account")}
              onClick={() => editDisplay("dashboardApplyUserSettings", !userOptions.dashboardApplyUserSettings)}
            />
            <Space />
          </ListItem>
        )}

        {currentDisplayTab === TAB_DISPLAY_USER && (
          <ListItem>
            <Checkbox
              active={benchmarkLocalVisible}
              text="Afficher les benchmarks sectoriels"
              onClick={() => {
                const next = !benchmarkLocalVisible
                setBenchmarkLocalVisible(next)
                localStorage.setItem("benchmark_visible", String(next))
                window.dispatchEvent(new CustomEvent("benchmark-visible-toggle"))
              }}
            />
            <Space />
          </ListItem>
        )}

        <ListItem>
          <Checkbox
            active={options.dashboardNightmareModeDisplay}
            text={t("dashboard_nigthtmare_label")}
            onClick={
              currentDisplayTab === TAB_DISPLAY_ACCOUNT || userOptions.dashboardApplyUserSettings
                ? () => editDisplay("dashboardNightmareModeDisplay", !options.dashboardNightmareModeDisplay)
                : null
            }
          />
          <Space />
        </ListItem>

        <ListItem>
          <Checkbox
            active={options.dashboardNightmareModeHeatmap}
            text={t("dashboard_nigthtmare_heatmap_label")}
            onClick={
              currentDisplayTab === TAB_DISPLAY_ACCOUNT || userOptions.dashboardApplyUserSettings
                ? () => editDisplay("dashboardNightmareModeHeatmap", !options.dashboardNightmareModeHeatmap)
                : null
            }
          />
          <Space />
        </ListItem>

        <ListItem>
          <div className="flex1">
            <div>{t("account_ceil_nightmare_note")}</div>
            <div className="height-20" />
            <MultiRangeSlider
              min={5}
              max={95}
              step={1}
              ruler={false}
              minValue={options.dashboardNightmareModeMin}
              maxValue={options.dashboardNightmareModeMax}
              onInput={(e: any) => editDisplay("dashboardNightmareMode", e)}
            />
          </div>
        </ListItem>

        {(options.dashboardNightmareModeDisplay || options.dashboardNightmareModeHeatmap) && (
          <ListItem>
            <div className="flex1">
              <div className="flex flex-wrap">
                <ColorPickerWidget
                  colors={SESSION_COLORS}
                  isCustom
                  title={t("score_below_color", { score: options.dashboardNightmareModeMin })}
                  selectedColor={options.dashboardNightmareColor1}
                  onSelect={(color: string) => editDisplay("dashboardNightmareColor1", color)}
                />

                <Space />

                <ColorPickerWidget
                  colors={SESSION_COLORS}
                  isCustom
                  title={t("score_between_color", {
                    score1: options.dashboardNightmareModeMin,
                    score2: options.dashboardNightmareModeMax
                  })}
                  selectedColor={options.dashboardNightmareColor2}
                  onSelect={(color: string) => editDisplay("dashboardNightmareColor2", color)}
                />

                <Space />

                <ColorPickerWidget
                  colors={SESSION_COLORS}
                  isCustom
                  title={t("score_above_color", { score: options.dashboardNightmareModeMax })}
                  selectedColor={options.dashboardNightmareColor3}
                  onSelect={(color: string) => editDisplay("dashboardNightmareColor3", color)}
                />
              </div>
            </div>
          </ListItem>
        )}

        <ListItem>
          <Dropdown
            displayField="name"
            isDisabled={currentDisplayTab === TAB_DISPLAY_USER && !userOptions.dashboardApplyUserSettings}
            title={t("score_display_mode")}
            list={SCORE_DISPLAY_MODE}
            value={t("score_display_" + options.dashboardDisplayMode)}
            onSelect={(e) => editDisplay("dashboardDisplayMode", e.id)}
          />
        </ListItem>

        <ListItem>
          <Checkbox
            active={options.hideNoAnswers}
            onClick={
              currentDisplayTab === TAB_DISPLAY_ACCOUNT || userOptions.dashboardApplyUserSettings
                ? (e) => editDisplay("hideNoAnswers", e.value)
                : null
            }
            text={t("account_hide_no_answers")}
          />
          <Space />
        </ListItem>

        <ListItem>
          <div>
            <Checkbox
              active={options.dashboardNeutralSeparated}
              onClick={
                currentDisplayTab === TAB_DISPLAY_ACCOUNT || userOptions.dashboardApplyUserSettings
                  ? (e) => editDisplay("dashboardNeutralSeparated", e.value)
                  : null
              }
              text={t("account_neutral_separated")}
            />
            <div className="grey-t text-sm" style={{ marginLeft: 36 }}>
              {t("account_neutral_separated_help")}
            </div>
            <div style={{ marginLeft: 36 }}>
              <NeutralGaugePreview isSeparated={options.dashboardNeutralSeparated} />
            </div>
          </div>
          <Space />
        </ListItem>

        <ListItem>
          <Checkbox
            active={options.hideStartedForParticipation}
            onClick={
              currentDisplayTab === TAB_DISPLAY_ACCOUNT || userOptions.dashboardApplyUserSettings
                ? (e) => editDisplay("hideStartedForParticipation", e.value)
                : null
            }
            text={t("account_hide_started_for_participation")}
          />
          <Space />
        </ListItem>

        <ListItem>
          {currentDisplayTab === TAB_DISPLAY_USER && !userOptions.dashboardApplyUserSettings && (
            <div
              className="abs"
              style={{
                width: "100%",
                height: "100%",
                backgroundColor: "#ffffff7a",
                zIndex: 9,
                top: 0,
                left: 0
              }}
            />
          )}

          <div className="flex1 min-w-0">
            <Checkbox
              active={isRepartitionCustom(options)}
              onClick={
                currentDisplayTab === TAB_DISPLAY_ACCOUNT || userOptions.dashboardApplyUserSettings
                  ? (e) => editDisplay("ceilsRepartitionCustom", e.value)
                  : null
              }
              text={t("account_ceil_repartition")}
            />

            {isRepartitionCustom(options) && (
              <>
                <div className="height-20" />

                <AdmSettingsRepartitionScaleWidget
                  ceils={options.ceilsRepartition}
                  labels={options.ceilsRepartitionLabels}
                  isGollac={false}
                  title={t("account_ceil_repartition_scale_standard")}
                  onChange={(ceils: number[], labels: string[]) =>
                    editDisplay("ceilsRepartitionScale", { ceils, labels })
                  }
                />

                <div className="height-20" />

                <AdmSettingsRepartitionScaleWidget
                  ceils={options.ceilsRepartitionGollac}
                  labels={options.ceilsRepartitionGollacLabels}
                  isGollac
                  title={t("account_ceil_repartition_scale_gollac")}
                  onChange={(ceils: number[], labels: string[]) =>
                    editDisplay("ceilsRepartitionGollacScale", { ceils, labels })
                  }
                />
              </>
            )}
          </div>
        </ListItem>

        {currentDisplayTab === TAB_DISPLAY_ACCOUNT && (
          <div>
            <ListItem>
              <Checkbox
                active={accountOptions.allowObserverSettings}
                onClick={(e) => edit("allowObserverSettings", e.value)}
                text={t("account_settings_observer")}
              />
              <Space />
            </ListItem>
          </div>
        )}

        {props._session.getModuleObserver("assistant") && (
          <ListItem>
            <Checkbox
              active={props._session.accountOptions.allowAssistant}
              text={t("consent_ai")}
              onClick={(e) => updateOptions("allowAssistant", e.value)}
            />
            <Space />
          </ListItem>
        )}

        <ListItem>
          <Checkbox
            active={props._session.accountOptions.emailDisplayCode}
            text={t("account_email_display_code")}
            onClick={(e) => updateOptions("emailDisplayCode", e.value)}
          />
          <Space />
        </ListItem>

        <ObserverFeaturesWidget
          options={props._session.accountOptions}
          onChange={(changes) => Object.entries(changes).forEach(([key, value]) => updateOptions(key, value))}
        />
      </div>
    )
  }

  async function save() {
    if (
      accountOptions.ceilsBirthDate.some(
        (ceil: number, index: number) =>
          !ceil || ceil < getBirthDateMinAllowed(index) || ceil > getBirthDateMaxAllowed(index)
      ) ||
      accountOptions.ceilsCompanyWelcomeDate.some(
        (ceil: number, index: number) =>
          !ceil || ceil < getCompanyWelcomeDateMinAllowed(index) || ceil > getCompanyWelcomeDateMaxAllowed(index)
      )
    ) {
      toast(t("ceil_date_error"), { type: "error" })
    } else {
      const mergedOptions = new AccountOptions({ ...props._session.accountOptions, ...accountOptions })
      store.dispatch(accountStatus(STATUS_SAVING))
      store.dispatch(sessionEdit("accountOptions", mergedOptions))
      store.dispatch(sessionEdit("userOptions", userOptions))
      await store.dispatch(accountUpdateOptions(mergedOptions))
      await store.dispatch(userUpdateOptions(props._session.userId, userOptions))
      store.dispatch(accountStatus(STATUS_SAVED))
      setIsEdited(false)
      navigate("/dashboard/home")
    }
  }

  return (
    <div>
      <Card isWithoutPadding>
        <ListItem>
          <div>
            {!props.isSmall && <h2>{t("account_settings_preferences_display")}</h2>}

            {props._session.userRole === "ADMIN" && (
              <Tabs
                tabs={{
                  TAB_DISPLAY_ACCOUNT: t("account_settings_preferences_account"),
                  TAB_DISPLAY_USER: t("account_settings_preferences_user")
                }}
                onClick={(tab) => setCurrentDisplayTab(tab)}
                active={currentDisplayTab}
              />
            )}
          </div>
        </ListItem>

        {getPreferencesDisplay()}
      </Card>

      {!props.isSmall && (
        <Card isWithoutPadding>
          <ListItem>
            <h2>{t("account_settings_preferences_dashboard")}</h2>
          </ListItem>

          <ListItem>
            <div className="flex1">
              <b>{t("account_settings_heatmap_display")}</b>

              <div className="flex gap-[24px] my-[16px]">
                <Dropdown
                  active={props._session.accountOptions.heatmapFilterName}
                  list={props.filter.list.map((filter) => ({
                    id: filter.name,
                    name: filter.name
                  }))}
                  value={props._session.accountOptions.heatmapFilterName}
                  displayField="name"
                  onSelect={(e) => updateOptions("heatmapFilterName", e.name)}
                  isNullAllowed
                  title={t("filter_reference")}
                  status={props.filter.status}
                />

                <Dropdown
                  active={props._session.accountOptions.heatmapResultsType}
                  list={ACCOUNT_RESULTS_TYPES.map((x) => ({ id: x, name: t(x) }))}
                  value={
                    props._session.accountOptions.heatmapResultsType
                      ? t(props._session.accountOptions.heatmapResultsType)
                      : null
                  }
                  displayField="name"
                  onSelect={(e) => updateOptions("heatmapResultsType", e.id)}
                  title={t("dashboard_heatmap_results_type")}
                />
              </div>

              <div className="flex flex-dcol gap-[16px]">
                <div className="flex">
                  <Checkbox
                    active={props._session.accountOptions.heatmapDisplayNoScore}
                    text={t("dashboard_heatmap_no_score")}
                    onClick={(e) => updateOptions("heatmapDisplayNoScore", e.value)}
                  />
                  <Space />
                </div>
                <div className="flex">
                  <Checkbox
                    active={props._session.accountOptions.heatmapDisplayParticipation}
                    text={t("dashboard_heatmap_participation")}
                    onClick={(e) => updateOptions("heatmapDisplayParticipation", e.value)}
                  />
                  <Space />
                </div>
                <div className="flex">
                  <Checkbox
                    active={props._session.accountOptions.heatmapDisplayCompany}
                    text={t("dashboard_heatmap_company")}
                    onClick={(e) => updateOptions("heatmapDisplayCompany", e.value)}
                  />
                  <Space />
                </div>
              </div>
            </div>
          </ListItem>

          <ListItem>
            <div className="flex1">
              <p>{t("ceils_birth_date_ceil_configure")}</p>

              <div className="flex flex-wrap">
                <div style={{ width: props.isSmall ? 300 : 500 }}>
                  <p style={{ fontSize: 12 }} className="grey-t">
                    {t("account_ceil_birth_date_title")}
                  </p>

                  {accountOptions.ceilsBirthDate.map((ceil: number, index: number) => (
                    <div key={index} className="flex">
                      <TextInput
                        min={getBirthDateMinAllowed(index)}
                        max={getBirthDateMaxAllowed(index)}
                        title={t("ceil_birth_date") + " " + (index + 1)}
                        error={!ceil || ceil < getBirthDateMinAllowed(index) || ceil > getBirthDateMaxAllowed(index)}
                        type={"number"}
                        value={ceil}
                        onChange={(event: any) =>
                          changeBirthDateIntervals(
                            accountOptions.ceilsBirthDate.map((x: number, i: number) =>
                              i === index ? parseInt(event.value, 10) : x
                            )
                          )
                        }
                      />

                      <div className="width-10" />

                      <div
                        className="_hover flex flex-dcol"
                        onClick={() =>
                          changeBirthDateIntervals(
                            accountOptions.ceilsBirthDate.filter((x: number, i: number) => i !== index)
                          )
                        }>
                        <div style={{ height: "37px" }} />
                        <Space />
                        {accountOptions.ceilsBirthDate.length > MIN_CEILS && (
                          <FontAwesomeIcon icon={faTimes} color="#111C2B" data-tip={t("ceil_birth_date_remove")} />
                        )}
                        <Space />
                      </div>
                    </div>
                  ))}

                  {accountOptions.ceilsBirthDate.length < MAX_CEILS && (
                    <u className="_hover grey-t" onClick={addBirthDateCeil}>
                      {"+ " + t("ceil_birth_date_add")}
                    </u>
                  )}
                </div>

                {!props.isSmall && <div className="width-100" />}

                {!props.isSmall && (
                  <div className="flex1">
                    <div className="flex">
                      <p className="grey-t">{t("account_ceil_birth_date_preview")}</p>
                      <Space />
                    </div>

                    <div style={{ maxWidth: "180px" }}>
                      {getUserFilterDates("BIRTH_DATE", new Date()).map((item: UserFilterDates) => (
                        <Chip key={item.id} isWithMargin>
                          <span>
                            {item.name + " "}
                            <FontAwesomeIcon icon={faInfoCircle} data-tip={item.description} />
                          </span>
                        </Chip>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </ListItem>

          <ListItem>
            <div className="flex1">
              <p>{t("ceils_company_welcome_date_ceil_configure")}</p>

              <div className="flex flex-wrap">
                <div style={{ width: props.isSmall ? 300 : 500 }}>
                  <p style={{ fontSize: 12 }} className="grey-t">
                    {t("account_ceil_company_welcome_date_title")}
                  </p>

                  {accountOptions.ceilsCompanyWelcomeDate.map((ceil: number, index: number) => (
                    <div key={index} className="flex">
                      <TextInput
                        min={getCompanyWelcomeDateMinAllowed(index)}
                        max={getCompanyWelcomeDateMaxAllowed(index)}
                        step={1}
                        title={t("ceil_company_welcome_date") + " " + (index + 1)}
                        error={
                          !ceil ||
                          ceil < getCompanyWelcomeDateMinAllowed(index) ||
                          ceil > getCompanyWelcomeDateMaxAllowed(index)
                        }
                        type={"number"}
                        value={ceil}
                        onChange={(event: any) =>
                          changeCompanyWelcomeDateIntervals(
                            accountOptions.ceilsCompanyWelcomeDate.map((x: number, i: number) =>
                              i === index ? parseInt(event.value, 10) : x
                            )
                          )
                        }
                      />

                      <div className="width-10" />

                      <div
                        className="_hover flex flex-dcol"
                        onClick={() =>
                          changeCompanyWelcomeDateIntervals(
                            accountOptions.ceilsCompanyWelcomeDate.filter((x: number, i: number) => i !== index)
                          )
                        }>
                        <div style={{ height: "37px" }} />
                        <Space />
                        {accountOptions.ceilsCompanyWelcomeDate.length > MIN_CEILS && (
                          <FontAwesomeIcon
                            icon={faTimes}
                            color="#111C2B"
                            data-tip={t("ceil_company_welcome_date_remove")}
                          />
                        )}
                        <Space />
                      </div>
                    </div>
                  ))}

                  {accountOptions.ceilsCompanyWelcomeDate.length < MAX_CEILS && (
                    <u className="_hover grey-t" onClick={addCompanyWelcomeDateCeil}>
                      {"+ " + t("ceil_company_welcome_date_add")}
                    </u>
                  )}
                </div>

                {!props.isSmall && <div className="width-100" />}

                {!props.isSmall && (
                  <div className="flex1">
                    <div className="flex">
                      <p className="grey-t">{t("account_ceil_company_welcome_date_preview")}</p>
                      <Space />
                    </div>

                    <div style={{ maxWidth: "180px" }}>
                      {getUserFilterDates("COMPANY_WELCOME_DATE", new Date()).map((item: UserFilterDates) => (
                        <Chip key={item.id} isWithMargin>
                          <span>
                            {item.name + " "}
                            <FontAwesomeIcon icon={faInfoCircle} data-tip={item.description} />
                          </span>
                        </Chip>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </ListItem>

          <ListItem>
            <div className="flex1">
              <p>{t("topic_repartition_ceil")}</p>
              <Range
                onChange={(value) => editDisplay("topicRepartitionCeil", value * 10)}
                min={0}
                max={10}
                step={0.5}
                value={accountOptions.topicRepartitionCeil / 10}
              />
            </div>
          </ListItem>

          <ListItem>
            <div>
              {t("filter_order")}
              <div className="height-10" />
              <AdmFiltersOrderWidget />
            </div>
          </ListItem>

          <ListItem>
            <AdmFiltersDefaultWidget />
          </ListItem>

          <ListItem>
            <GollacFiltersWidget />
          </ListItem>
        </Card>
      )}

      {isEdited && (
        <SaveToast isSaveActive status={props.account.status as StatusType} onCancel={cancel} onSave={save} />
      )}
    </div>
  )
}

const mapStateToProps = (state) => ({
  account: state.account,
  filter: state.filter,
  _session: state._session
})

export default connect(mapStateToProps)(withTranslation()(AdmSettingsSurveyWidget))
